export class Autocomplete {
    static HTML_CLASS = 'rb-autocomplete';
    static HTML_CONTAINER_CLASS = 'rb-autocomplete-container';
    static HTML_SELECTED_CLASS = 'rb-autocomplete-selected';

    constructor(el) {
        // Validate the element
        this.validate(el);
        // Set the element
        this.el = el;
        // Set the source
        const source = this.el.getAttribute('data-autocomplete-source');
        // Transform the source to an array
        if (source) {
            this.source = source.split(',');
        }
        // Set the url
        this.url = null;
        if (typeof this.el.getAttribute('data-autocomplete-url') !== 'undefined') {
            this.url = this.el.getAttribute('data-autocomplete-url');
        }
        this.urlParams = null;
        if (typeof this.el.getAttribute('data-autocomplete-url-params') !== 'undefined') {
            const urlParams = this.el.getAttribute('data-autocomplete-url-params');
            // set to urlParams. If is parasable, take the parsed JSON instead.
            this.urlParams = urlParams && JSON.parse(urlParams);
        }

        this._alreadyFetchedData = [];

        // Set the min chars
        this.minChars = parseInt(this.el.getAttribute('data-autocomplete-min-chars')) || 1;

        // Set the delay
        this.delay = parseInt(this.el.getAttribute('data-autocomplete-delay')) || 0;

        // autocomplete-enter-on-click
        this.enterOnClick = this.el.getAttribute('data-autocomplete-enter-on-click') === 'true';
    }

    /**
     * Validate the element
     * @param {HTMLElement} el
     */
    validate(el) {
        if (el.tagName !== 'INPUT') {
            throw new Error('Autocomplete can only be applied to an input element');
        }
        // Warn if the element doesn't have autocomplete="off"
        if (el.getAttribute('autocomplete') !== 'off') {
            console.warn('Autocomplete should be set to "off" on the input element');
        }
        // Check if the element has data-autcomplete-url or data-autocomplete-source
        if (!el.hasAttribute('data-autocomplete-url') && !el.hasAttribute('data-autocomplete-source')) {
            throw new Error('Autocomplete requires either data-autocomplete-url or data-autocomplete-source');
        }
    }

    /**
     * Initialize the autocomplete
     */
    init() {
        // Create the autocomplete container
        this.createContainer();
        // Add the event listeners
        this.addEventListeners();
    }

    /**
     * Create the autocomplete container
     */
    createContainer() {
        // Check if the container already exists in the DOM
        this.container = this.el.parentNode.querySelector('.' + Autocomplete.HTML_CONTAINER_CLASS);
        // If the container doesn't exist
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.classList.add(Autocomplete.HTML_CONTAINER_CLASS);
            // Append the container to the body
            document.body.appendChild(this.container);

            // On click outside the container element hide the container
            document.addEventListener('click', (e) => {
                if (!this.container.contains(e.target)) {
                    this.hideContainer();
                }
            });
        }
    }

    /**
     * Add the event listeners
     */
    addEventListeners() {
        // Add the input event listener
        this.el.addEventListener('input', (e) => this.onInput(e));
        // Add the keydown event listener
        this.el.addEventListener('keydown', (e) => this.onKeyDown(e));
    }

    /**
     * On input event listener
     */
    onInput(e) {
        // Get the value
        const value = e.target.value;
        // Check if the value is long enough
        if (value.length < this.minChars) {
            // Hide the container
            this.hideContainer();
            // Return
            return;
        }
        // Check if the value is the same as the last value
        if (value === this.lastValue) {
            // Return
            return;
        }
        // Set the last value
        this.lastValue = value;
        // Check if the delay is set
        if (this.delay) {
            // Clear the timeout
            clearTimeout(this.timeout);
            // Set the timeout
            this.timeout = setTimeout(this.fetch.bind(this), this.delay);
        } else {
            // Fetch the data
            this.fetch();
        }
    }

    /**
     * Fetch the data
     */
    async fetch() {
        // Check if the url is set
        if (this.url !== null) {
            // Fetch the data
            await this._currentFetchPromise;
            this.fetchUrl();
        } else {
            // Fetch the data
            this.fetchSource();
        }
    }

    /**
     * Fetch the source
     */
    fetchSource() {
        // Get the value
        const value = this.el.value;
        // Filter the source
        const filtered = this.source.filter((item) => item.toLowerCase().indexOf(value.toLowerCase()) !== -1);
        // Check if there are any results
        if (filtered.length === 0) {
            // Hide the container
            this.hideContainer();
            // Return
            return;
        }
        // Render the results
        this.renderResults(filtered);
    }

    /**
     * Fetch the url
     */
    fetchUrl() {
        // Get the value
        const value = this.el.value;
        // search in _alreadyFetchedData
        let alreadyFetched = this._alreadyFetchedData[value] || [];
        let needFetch = false;
        // or any of the alreadyFetched keys in the value
        if (alreadyFetched.length == 0) {
            needFetch = true;
            for (const key in this._alreadyFetchedData) {
                if (value.indexOf(key) !== -1) {
                    alreadyFetched = this._alreadyFetchedData[key];
                    needFetch = false;
                    break;
                }
            }
        }
        // Filter the source
        const filtered = alreadyFetched.filter((item) => item.toLowerCase().indexOf(value.toLowerCase()) !== -1);
        // Check if there are any results
        if (filtered.length > 0) {
            // Render the results
            this.renderResults(filtered);
        } 
        // if lastValue is not in value
        if (needFetch) {
            // Create the url
            let url = this.url + '?term=' + value;
            // Check if there are any url params
            if (Object.keys(this.urlParams).length) {
                // Loop through the url params
                for (const key in this.urlParams) {
                    // Add the url param
                    url += '&' + key + '=' + this.urlParams[key];
                }
            }
            // Fetch the data
            this._currentFetchPromise = fetch(url)
                .then((response) => response.json())
                .then((data) => {
                    // Check if there are any results
                    if (data.length === 0) {
                        // Hide the container
                        this.hideContainer();
                        // Return
                        return;
                    }
                    // Render the results
                    this.renderResults(data);
                    // add to _alreadyFetchedData
                    this._alreadyFetchedData[value] = data;
                });
        } else if (filtered.length === 0) {
            // Hide the container
            this.hideContainer();
        }
    }


    /**
     * Hide the container
     */
    hideContainer() {
        // Hide the container
        this.container.style.display = 'none';

        // Remove class autocomplete-opened from the input
        this.el.classList.remove('autocomplete-opened');

        // If this.el has parent with classy class
        if (this.el.closest('.classy')) {
            // Remove class classy-opened from the parent
            this.el.closest('.classy').classList.remove('autocomplete-opened');
        }
    }

    /**
     * Render the results
     */
    renderResults(results) {
        // Clear the container
        this.container.innerHTML = '';
        // Loop through the results
        results.forEach((result) => {
            // Create the element
            const el = document.createElement('li');
            // Set the innerHTML
            el.innerHTML = result;
            // Add the click event listener
            el.addEventListener('click', (e) => this.onResultClick(e));
            // Append the element to the container
            this.container.appendChild(el);
        });
        // Show the container
        this.showContainer();
    }

    /**
     * Show the container
     */
    showContainer() {
        // Get the position of the input element
        const position = this.el.getBoundingClientRect();
        // Set the position of the container
        this.container.style.top = ((position.top + position.height) * ViewportChildrenScale) + 'px';
        this.container.style.left = ((position.left - 2) * ViewportChildrenScale) + 'px';
        // Show the container
        this.container.style.display = 'block';
        // Set the position of the container
        this.container.style.position = 'absolute';
        // Set the width of the container
        this.container.style.width = ((position.width + 4) * ViewportChildrenScale) + 'px';

        // Add class autocomplete-opened to input
        this.el.classList.add('autocomplete-opened');

        // If this.el has parent with classy class
        if (this.el.closest('.classy')) {
            // Add class classy-opened to parent
            this.el.closest('.classy').classList.add('autocomplete-opened');
        }
    }

    /**
     * On keydown event
     */
    onKeyDown(e) {
        // Check if the container is visible
        if (this.container.style.display === 'none') {
            // Return
            return;
        }
        // Check if the key is down
        if (e.key === 'ArrowDown') {
            // Prevent the default
            e.preventDefault();
            // Focus the next element
            this.focusNextElement();
        }
        // Check if the key is up
        if (e.key === 'ArrowUp') {
            // Prevent the default
            e.preventDefault();
            // Focus the previous element
            this.focusPreviousElement();
        }
        // Check if the key is enter
        if (e.key === 'Enter') {
            // Prevent the default
            e.preventDefault();
            // Focus the selected element
            this.focusSelectedElement();
        }
    }

    /**
     * Focus the selected element
     */
    focusSelectedElement() {
        // Get the selected element
        const el = this.container.querySelector('.' + Autocomplete.HTML_SELECTED_CLASS);
        // Check if the element exists
        if (!el) {
            // Return
            return;
        }
        // Set the value
        this.setValue(el.innerHTML);
    }

    /**
     * Focus the next element
     */
    focusNextElement() {
        // Get the selected element
        const el = this.container.querySelector('.' + Autocomplete.HTML_SELECTED_CLASS);
        // Check if the element exists
        if (!el) {
            // Focus the first element
            this.focusFirstElement();
            // Return
            return;
        }
        // Get the next element
        const next = el.nextElementSibling;
        // Check if the next element exists
        if (!next) {
            // Return
            return;
        }
        // Remove the selected class from the selected element
        el.classList.remove(Autocomplete.HTML_SELECTED_CLASS);
        // Add the selected class to the next element
        next.classList.add(Autocomplete.HTML_SELECTED_CLASS);
    }

    /**
     * Focus first element
     */
    focusFirstElement() {
        // Get the first element
        const el = this.container.querySelector('li');
        // Check if the element exists
        if (!el) {
            // Return
            return;
        }
        // Add the selected class to the element
        el.classList.add(Autocomplete.HTML_SELECTED_CLASS);
    }

    /**
     * Focus the previous element
     */
    focusPreviousElement() {
        // Get the selected element
        const el = this.container.querySelector('.' + Autocomplete.HTML_SELECTED_CLASS);
        // Check if the element exists
        if (!el) {
            // Focus the last element
            this.focusLastElement();
            // Return
            return;
        }
        // Get the previous element
        const previous = el.previousElementSibling;
        // Check if the previous element exists
        if (!previous) {
            // Return
            return;
        }
        // Remove the selected class from the selected element
        el.classList.remove(Autocomplete.HTML_SELECTED_CLASS);
        // Add the selected class to the previous element
        previous.classList.add(Autocomplete.HTML_SELECTED_CLASS);
    }

    /**
     * onResultClick
     */
    onResultClick(e) {
        // Set the value
        this.setValue(e.target.innerHTML);
    }

    setValue(value) {
        this.el.value = value;
        // if enterOnClick is true
        if (this.enterOnClick) {
            $(this.el).submit(); // TODO: Change this to a vanilla JS solution
        }
        // Hide the container
        this.hideContainer();
    }
}
